/**
 * [BLURY] | 🥭 GrainPanel
 * The grain pass: amount, tile coarseness, density, blend mode and colour mode.
 * @build 13.09.26
 * @updated 13.09.26 PHT
 * @author TONYTONY Sàrl
 */
import type { GrainBlend, GrainType } from '@/engine/types';
import { useStudio } from '@/state/useStudio';
import { Section } from '../ui/Section';
import { Segmented } from '../ui/Segmented';
import { Select } from '../ui/Select';
import { Row, RowLabel, Slider } from '../ui/Slider';

const BLEND_MODES: GrainBlend[] = ['overlay', 'soft-light', 'screen', 'multiply', 'luminosity'];

const GRAIN_TYPES: ReadonlyArray<{ value: GrainType; label: string }> = [
  { value: 'mono', label: 'mono' },
  { value: 'color', label: 'colour' },
];

export function GrainPanel() {
  const composition = useStudio(s => s.composition);
  const setNumeric = useStudio(s => s.setNumeric);
  const setGrainBlend = useStudio(s => s.setGrainBlend);
  const setGrainType = useStudio(s => s.setGrainType);

  return (
    <Section badge={3} title="Grain">
      <Slider
        label="amount"
        value={composition.grain}
        min={0}
        max={100}
        format={v => `${v}%`}
        onChange={v => setNumeric('grain', v)}
      />
      <Slider
        label="coarseness"
        value={composition.gsize}
        min={1}
        max={8}
        format={v => `${v} px`}
        onChange={v => setNumeric('gsize', v)}
      />
      <Slider
        label="density"
        value={composition.gdens}
        min={5}
        max={100}
        format={v => `${v}%`}
        onChange={v => setNumeric('gdens', v)}
      />

      <Row>
        <RowLabel name="blend" />
        <Select
          aria-label="grain blend mode"
          value={composition.gblend}
          onChange={e => setGrainBlend(e.target.value as GrainBlend)}
        >
          {BLEND_MODES.map(mode => (
            <option key={mode} value={mode}>
              {mode}
            </option>
          ))}
        </Select>
      </Row>

      <Row>
        <RowLabel name="type" />
        <Segmented
          aria-label="grain type"
          value={composition.gtype}
          options={GRAIN_TYPES}
          onChange={setGrainType}
        />
      </Row>
    </Section>
  );
}
